import { useEffect, useMemo, useState } from 'react';
import { ServerStatusNotice } from '@/components/common/ServerStatusNotice';
import { useNavigate } from 'react-router-dom';
import useSWR, { mutate as swrMutate } from 'swr';
import { isAxiosError } from 'axios';
import { ChevronLeft, ChevronRight, Copy, Link2Off, Loader2, Plus, Search } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useT } from '@/contexts/I18nContext';
import { usePageTitle } from '@/contexts/PageHeaderContext';
import { cn } from '@/lib/utils';
import {
  issueDiscoveryToken,
  listLocalComputers,
  type LocalComputer,
  type SetupStage,
} from '@/api/localComputers';
import { extractApiError } from '@/utils/apiError';

const PAGE_SIZE = 50;

export default function LocalComputersListPage() {
  const { t } = useT();
  const navigate = useNavigate();
  usePageTitle(t('admin.lc.title'), t('admin.lc.subtitle'));

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(query.trim());
      setOffset(0);
    }, 200);
    return () => window.clearTimeout(id);
  }, [query]);

  const swrKey = `admin-local-computers:${debounced}:${offset}:${PAGE_SIZE}`;
  const { data, error, isLoading } = useSWR(swrKey, () =>
    listLocalComputers({ q: debounced || undefined, limit: PAGE_SIZE, offset }).then(r => r.data),
  );

  // Per xxvii (task #82): clicking a row navigates to the new route
  // page `/admin/local-computers/:id` rather than popping a modal.
  // Deep-linking + browser-back + the discovery flow's `detail_url`
  // all now work uniformly through the URL.
  const [discoveryDialogOpen, setDiscoveryDialogOpen] = useState(false);
  const reloadList = () => swrMutate(key => typeof key === 'string' && key.startsWith('admin-local-computers:'));

  const total = data?.count ?? 0;
  const results = data?.results ?? [];
  const hasPrev = offset > 0;
  const hasNext = offset + PAGE_SIZE < total;

  return (
    <div className="container mx-auto max-w-6xl space-y-4 px-4 py-6">
      <Card variant="section">
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between gap-3">
            <div className="relative w-full max-w-md">
              <Search className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder={t('admin.lc.searchPlaceholder')}
                className="h-9 w-full rounded-md border bg-background pl-8 pr-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:border-ring"
                autoComplete="off"
              />
            </div>
            <Button size="sm" onClick={() => setDiscoveryDialogOpen(true)}>
              <Plus className="size-3.5" />
              {t('admin.lc.discoverAction')}
            </Button>
          </div>

          {error && !data && <ServerStatusNotice variant="card" error={error} />}

          <LocalComputersTable
            rows={results}
            loading={isLoading}
            t={t}
            onRowClick={lc => navigate(`/admin/local-computers/${lc.id}`)}
          />

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {total === 0
                ? t('admin.lc.empty')
                : t('admin.users.pageOf', {
                    from: offset + 1,
                    to: Math.min(offset + PAGE_SIZE, total),
                    total,
                  })}
            </span>
            <div className="flex gap-1">
              <Button
                variant="outline"
                size="icon-sm"
                disabled={!hasPrev || isLoading}
                onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))}
                aria-label={t('admin.users.prev')}
              >
                <ChevronLeft className="size-3.5" />
              </Button>
              <Button
                variant="outline"
                size="icon-sm"
                disabled={!hasNext || isLoading}
                onClick={() => setOffset(o => o + PAGE_SIZE)}
                aria-label={t('admin.users.next')}
              >
                <ChevronRight className="size-3.5" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* LocalComputerDetailModal lifted out — detail now lives at
          /admin/local-computers/:id (xxvii / task #82). The list page
          navigates there on row click. */}

      <DiscoveryDialog
        open={discoveryDialogOpen}
        onClose={() => setDiscoveryDialogOpen(false)}
        knownIds={new Set(results.map(lc => lc.id))}
        onDiscovered={lcId => {
          setDiscoveryDialogOpen(false);
          reloadList();
          navigate(`/admin/local-computers/${lcId}`);
        }}
        t={t}
      />
    </div>
  );
}

interface TableProps {
  rows: LocalComputer[];
  loading: boolean;
  t: (k: string, p?: Record<string, string | number>) => string;
  onRowClick: (lc: LocalComputer) => void;
}

function LocalComputersTable({ rows, loading, t, onRowClick }: TableProps) {
  const columns = useMemo(
    () => [
      { key: 'num', label: t('admin.lc.colDeviceNumber') },
      { key: 'name', label: t('admin.lc.colName') },
      { key: 'hostname', label: t('admin.lc.colHostname') },
      { key: 'wg', label: t('admin.lc.colWgIp') },
      { key: 'ssh', label: t('admin.lc.colSshPort') },
      { key: 'hw', label: t('admin.lc.colHardware') },
      { key: 'stage', label: t('admin.lc.colStage') },
      // 'hb' (heartbeat) column removed in task #82 / xxvii — BE
      // dropped `last_heartbeat_at`; Prometheus replaces.
    ],
    [t],
  );

  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
          <tr>
            {columns.map(c => (
              <th key={c.key} className="px-3 py-2 text-left font-semibold">{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-3 py-6 text-center text-muted-foreground">
                <span className="inline-flex items-center gap-2">
                  <Loader2 className="size-4 animate-spin" /> {t('common.loading')}
                </span>
              </td>
            </tr>
          )}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={columns.length} className="px-3 py-10 text-center">
                <div className="mx-auto max-w-md space-y-2 text-sm text-muted-foreground">
                  <div>{t('admin.lc.empty')}</div>
                  <div className="text-xs">{t('admin.lc.emptyHint')}</div>
                </div>
              </td>
            </tr>
          )}
          {rows.map(lc => (
            <tr
              key={lc.id}
              onClick={() => onRowClick(lc)}
              className={cn(
                // `[&_td]:cursor-pointer` overrides the I-beam that text-
                // content cells default to — without it the cursor flicks
                // between pointer / text as the operator moves across the
                // row. Whole row is one click target → one cursor.
                'cursor-pointer border-t [&_td]:cursor-pointer',
                lc.deployment_key === null
                  ? 'bg-amber-50 hover:bg-amber-100/70 dark:bg-amber-950/30 dark:hover:bg-amber-950/50'
                  : 'hover:bg-muted/30',
              )}
            >
              <td className="px-3 py-2 font-mono text-sm tabular-nums">
                {lc.device_number ?? <span className="text-muted-foreground">—</span>}
              </td>
              <td className="px-3 py-2 font-medium">
                {lc.deployment_key === null ? (
                  <div className="space-y-1">
                    <Badge variant="warning" className="gap-1" title={t('admin.lc.unboundHint')}>
                      <Link2Off className="size-3" /> {t('admin.lc.unbound')}
                    </Badge>
                    <div className="text-xs text-muted-foreground">
                      {lc.display_name || lc.hostname || lc.wg_ip}
                    </div>
                  </div>
                ) : (
                  <>
                    <div>{lc.display_name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{lc.deployment_key}</div>
                  </>
                )}
              </td>
              <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{lc.hostname}</td>
              <td className="px-3 py-2 font-mono text-xs tabular-nums">{lc.wg_ip}</td>
              <td className="px-3 py-2 font-mono text-xs tabular-nums text-muted-foreground">
                {lc.reverse_ssh_port ?? '—'}
              </td>
              <td className="px-3 py-2 text-xs text-muted-foreground">{lc.hardware_model}</td>
              <td className="px-3 py-2">
                <StageBadge stage={lc.setup_stage} isArchived={lc.is_archived} t={t} />
              </td>
              {/* Heartbeat column removed (xxvii / task #82). */}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StageBadge({
  stage,
  isArchived,
  t,
}: {
  stage: SetupStage;
  isArchived: boolean;
  t: (k: string) => string;
}) {
  // Numeric "N/9" suffix dropped per team-lead's 4i polish call:
  // it read as "step N done" on freshly-registered rows where the
  // jetson hasn't run anything yet. The SetupStageChecklist row
  // positions carry the progress meaning; the badge just labels.
  const isComplete = stage === 'DOCKER_RUNNING' && !isArchived;
  const variant: 'success' | 'warning' | 'muted' =
    isComplete ? 'success' : isArchived ? 'muted' : 'warning';
  return (
    <Badge variant={variant} className="font-normal">
      {t(`admin.lc.stage.${stage}`)}
    </Badge>
  );
}

// BindDialog removed — bind affordance now lives inside LocalComputerDetailModal.
// IssueTokenDialog removed in task #82 / xxvii — replaced by DiscoveryDialog
// (token-less, no inputs; operator paste-runs the curl and a new LC row
// appears in the list via the discovery POST).

interface DiscoveryDialogProps {
  open: boolean;
  onClose: () => void;
  /** IDs visible in the list at the moment the dialog opened — used
   *  to detect a newly-arrived LC after the operator runs the curl. */
  knownIds: Set<string>;
  /** Called when polling detects a new LC; the parent navigates to
   *  the detail page for it. */
  onDiscovered: (lcId: string) => void;
  t: (k: string, p?: Record<string, string | number>) => string;
}

function DiscoveryDialog({ open, onClose, knownIds, onDiscovered, t }: DiscoveryDialogProps) {
  const [copied, setCopied] = useState(false);
  // Discovery-token curl is BE-rendered post-7e908b1 — operator
  // copies it verbatim. Token is single-use + 24h expiry; if it
  // expires mid-modal-session, a re-open re-issues. We fetch on
  // open (not on render) so the token isn't burned by mere
  // dialog construction.
  const [curl, setCurl] = useState<string | null>(null);
  const [issueError, setIssueError] = useState<string | null>(null);

  // Reset state on re-open + issue a fresh token.
  useEffect(() => {
    if (!open) return;
    setCopied(false);
    setCurl(null);
    setIssueError(null);
    let cancelled = false;
    (async () => {
      try {
        const { data } = await issueDiscoveryToken();
        if (cancelled) return;
        setCurl(data.discovery_curl);
      } catch (e) {
        if (cancelled) return;
        setIssueError(
          isAxiosError(e) ? extractApiError(e, t('error.general')) : t('error.general'),
        );
      }
    })();
    return () => { cancelled = true; };
  }, [open, t]);

  // Poll the list every 3s while open. As soon as an LC id appears
  // that wasn't in `knownIds` at open time, navigate to it — no
  // operator click required. Per Alexey 2026-05-23: no refresh
  // button, auto-redirect.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const resp = await listLocalComputers({ limit: 100, offset: 0 });
        if (cancelled) return;
        const newLc = resp.data.results.find(lc => !knownIds.has(lc.id));
        if (newLc) onDiscovered(newLc.id);
      } catch {
        // ignore transient errors; the next tick retries
      }
    };
    const handle = window.setInterval(tick, 3000);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [open, knownIds, onDiscovered]);

  const copy = async () => {
    if (!curl) return;
    try {
      await navigator.clipboard.writeText(curl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard.writeText fails in non-HTTPS dev contexts; ignore —
      // operator can still select the <pre> body manually.
    }
  };

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('admin.lc.discoveryDialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">
            {t('admin.lc.discoveryDialog.description')}
          </p>

          {issueError != null && (
            <div className="rounded-md bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {issueError}
            </div>
          )}
          <div className="relative">
            <pre className="overflow-x-auto rounded-md border bg-muted/40 p-3 font-mono text-xs leading-relaxed">
              {curl ?? (
                <span className="inline-flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="size-3.5 animate-spin" />
                  {t('admin.lc.discoveryDialog.issuingToken')}
                </span>
              )}
            </pre>
            <Button
              variant="outline"
              size="sm"
              className="absolute right-2 top-2"
              onClick={copy}
              disabled={!curl}
            >
              <Copy className="size-3.5" />
              {copied ? t('common.copied') : t('common.copy')}
            </Button>
          </div>

          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Loader2 className="size-3.5 animate-spin" />
            {t('admin.lc.discoveryDialog.waiting')}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── next-free helpers ───────────────────────────────────────────────

// `nextFreeDeviceNumber` + `nextFreeWgIp` are still exported from
// @/api/localComputers for the LC detail-page in-place editors (2026-05-21).
// The discovery flow doesn't use them — wg_ip + device_number are now
// operator-fillable AFTER the LC row exists, not upfront.

// `formatHeartbeat` helper removed (xxvii / task #82) — BE no longer
// tracks `last_heartbeat_at`, and the heartbeat column is gone from
// the LC table above.
